import { GameState } from "../../types";
import {
  BaseDecisionPriority,
  DecisionContext,
  DecisionResult,
  MoveInfo,
} from "./interfaces/DecisionPriority";
import { MCTSMoveGenerationService } from "../utils/MCTSMoveGenerationService";

// FORCED MOVES (1-15) - Early exit priorities
import { RaceVictoryPriority } from "./priorities/02_clear-priorities/01_RaceVictory";
import { KingslayerVictoryPriority } from "./priorities/02_clear-priorities/02_KingslayerVictory";
import { RaceBlockPriority } from "./priorities/02_clear-priorities/03_RaceBlock";
import { DontSetupRacePriority } from "./priorities/02_clear-priorities/04_DontSetupRace";
import { RaceDisruptPriority } from "./priorities/02_clear-priorities/05_RaceDisrupt";
import { KingSafetyMovePriority } from "./priorities/02_clear-priorities/06_KingSafetyMove";
import { KingSafetyStayPriority } from "./priorities/02_clear-priorities/07_KingSafetyStay";
import { RaceBlockRemainPriority } from "./priorities/02_clear-priorities/08_RaceBlockRemain";
import { TwoTurnDefenceStayPriority } from "./priorities/02_clear-priorities/11_TwoTurnDefenceStay";
import { TwoTurnRacePriority } from "./priorities/02_clear-priorities/09_TwoTurnRace";
import { TwoTurnRaceBlockPriority } from "./priorities/02_clear-priorities/10_TwoTurnRaceBlock";
import { TwoTurnKingDefencePriority } from "./priorities/02_clear-priorities/12_TwoTurnKingDefence";
import { TwoTurnKingCapturePriority } from "./priorities/02_clear-priorities/13_TwoTurnKingCapture";

// MINIMAX EVALUATION PRIORITIES
import { KingtradepushPriority } from "./priorities/04_minimax-evaluation/14_Kingtradepush";
import { HangingPiecesPrevention } from "./priorities/04_minimax-evaluation/15_Hanging";
import { SelfCaptureBlockPriority } from "./priorities/04_minimax-evaluation/16_SelfCaptureBlock";
import { FreeCapturePriority } from "./priorities/04_minimax-evaluation/17_FreeCapture";
import { HangStayPriority } from "./priorities/04_minimax-evaluation/18_HangStay";

import { FreeThreats } from "./priorities/04_minimax-evaluation/19_FreeThreats";
import { TwoTurnFreeCapture } from "./priorities/04_minimax-evaluation/20_TwoTurnFreeCapture";
import { TwoTurnCaptureDefence } from "./priorities/04_minimax-evaluation/21_TwoTurnCaptureDefence";
import { ImmediateForksPins } from "./priorities/04_minimax-evaluation/21_ImmediateForksPins";
import { TwoTurnForksPins } from "./priorities/04_minimax-evaluation/22_TwoTurnForksPins";
import { TradePriority } from "./priorities/04_minimax-evaluation/23_Trade";

// ABSTRACT PRIORITY (piece-specific heuristics)
import { PieceSpecificHeuristicsPriority } from "./priorities/03_abstract-priorities/24_PieceSpecificHeuristics";

// MCTS FALLBACK PRIORITIES
import { KingBackupSupport } from "./priorities/05_mcts-fallback/24_KingBackupSupport";
import { StrategicAdvancePriority } from "./priorities/05_mcts-fallback/25_StrategicAdvance";
import { BackupSupportPriority } from "./priorities/05_mcts-fallback/26_BackupSupport";
import { PositionalAdvancement } from "./priorities/05_mcts-fallback/27_PositionalAdvancement";
import { EndgameKingAdvance } from "./priorities/05_mcts-fallback/28_EndgameKingAdvance";

/**
 * MCTS Decision Engine Controller
 *
 * Complete 29-priority system (enhanced from original 27)
 *
 * FORCED MOVES (1-18) - Early exit if found:
 * 1. Race Victory - Take immediate race wins ✅ IMPLEMENTED
 * 2. Kingslayer Victory - Capture enemy king ✅ IMPLEMENTED
 * 3. Race Block - Block enemy 1-move wins ✅ IMPLEMENTED
 * 4. Don't Setup Race - Prevent moves enabling enemy race wins ✅ IMPLEMENTED
 * 5. Race Disrupt - Disrupt enemy race patterns ✅ IMPLEMENTED
 * 6. King Safety Move - Protect our king from danger ✅ IMPLEMENTED
 * 7. King Safety-Stay - Keep king safe when under new immediate danger ✅ IMPLEMENTED
 * 8. Race Block Remain - Keep immune blockers in defensive positions ✅ RESTORED
 * 9. Two Turn Race - Set up unblockable prerace ✅ IMPLEMENTED
 * 10. Two Turn Race Block - Block enemy 2-move wins ✅ IMPLEMENTED
 * 11. Two Turn Defence Stay - PROACTIVE: Prevent moves creating new 2-turn threats ✅ NEW
 * 12. Two Turn King Defence - 4-ply defense with defended piece logic ✅ ENHANCED
 * 13. Two Turn King Capture - Force king capture in 2 moves ✅ IMPLEMENTED
 * 14. King Trade Push - Force enemy king retreat with defendable threats ✅ IMPLEMENTED
 * 15. Hanging - Don't hang pieces for free ✅ IMPLEMENTED (moved down)
 * 16. Self Capture Block - Prevent capturing own pieces ✅ IMPLEMENTED
 * 17. Free Capture - Capture enemy pieces with no recapture ✅ IMPLEMENTED
 * 18. Hang-Stay Defense - Defensively move hanging pieces when materially down ✅ IMPLEMENTED
 *
 * EVALUATION MOVES (19-29) - MCTS weighting priorities
 * 19. Free Threats - Threaten pieces without retaliation ✅ IMPLEMENTED
 * 20. Two Turn Free Capture - Set up unstoppable 2-turn captures ✅ IMPLEMENTED
 * 21. Two Turn Capture Defence - Defend against inevitable 2-turn captures ✅ IMPLEMENTED
 * 22. Immediate Forks and Pins - Tactical advantages ✅ IMPLEMENTED
 * 23. Two Turn Forks and Pins - Future tactical setups ✅ IMPLEMENTED
 * 24. Trade - Beneficial material exchanges ✅ IMPLEMENTED
 * 25. King Backup Support - Move pieces to protect stuck king ✅ NEW
 * 26. Piece Specific Heuristics - Special piece behaviors ✅ IMPLEMENTED
 * 27. Strategic Advance - Improve positioning (PUCT priors) ✅ ENHANCED
 * 28. Backup Support - Defend advanced pieces ✅ IMPLEMENTED
 * 29. Positional Advancement - General advancement (simplified) ✅ ENHANCED
 */
export class MCTSDecisionController {
  private priorities: BaseDecisionPriority[] = [];
  private debugMode: boolean = false;
  private currentAnalysisLogs: string[] = [];

  constructor(debugMode: boolean = false) {
    this.debugMode = debugMode;
    this.initializePriorities();
  }

  private initializePriorities(): void {
    this.priorities = [
      // FORCED MOVES (1-15) - Early exit priorities
      new RaceVictoryPriority(), // 1
      new KingslayerVictoryPriority(), // 2
      new RaceBlockPriority(), // 3
      new DontSetupRacePriority(), // 4
      new RaceDisruptPriority(), // 5
      new KingSafetyMovePriority(), // 6 - King Safety
      new KingSafetyStayPriority(), // 7 - King Safety-Stay
      new RaceBlockRemainPriority(), // 8 - Keep immune blockers in defensive positions
      new TwoTurnRacePriority(), // 9
      new TwoTurnRaceBlockPriority(), // 10
      new TwoTurnDefenceStayPriority(), // 11 - PROACTIVE: Prevent moves that create new threats
      new TwoTurnKingDefencePriority(), // 12 - Focus defense when king threatened
      new TwoTurnKingCapturePriority(), // 13 - Force king capture in 2 moves
      new KingtradepushPriority(), // 14 - Force enemy king retreat with defendable threats
      new HangingPiecesPrevention(), // 15 - MOVED DOWN: After critical defense
      new SelfCaptureBlockPriority(), // 16
      new FreeCapturePriority(), // 17 - Free captures before hanging concerns
      new HangStayPriority(), // 18 - (currently file 16)

      // EVALUATION MOVES (19-27) - MCTS weighting priorities
      new FreeThreats(), // 19 - (currently file 15)
      new TwoTurnFreeCapture(), // 20 - Two Turn Free Capture
      new TwoTurnCaptureDefence(), // 21 - Two Turn Capture Defence
      new ImmediateForksPins(), // 22 - (currently file 17)
      new TwoTurnForksPins(), // 23 - (currently file 18)
      new TradePriority(), // 24
      new KingBackupSupport(), // 25 - King Backup Support
      new PieceSpecificHeuristicsPriority(), // 26 - (previously 24)
      new StrategicAdvancePriority(), // 27 - (previously 25) - Now PUCT priors
      new BackupSupportPriority(), // 28 - (previously 26)
      new PositionalAdvancement(), // 29 - (previously 27) - Simplified
      new EndgameKingAdvance(),
    ];

    // Sort by priority (lower number = higher priority)
    this.priorities.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Main decision function - analyzes position and returns best decision
   */
  public makeDecision(
    gameState: GameState,
    allPossibleMoves: MoveInfo[],
    currentTurn: number,
    timeRemaining?: number
  ): DecisionResult {
    // Reset logs for this analysis
    this.currentAnalysisLogs = [];

    const context: DecisionContext = {
      gameState,
      allPossibleMoves,
      currentTurn,
      timeRemaining,
      debugMode: this.debugMode,
    };

    // Debug logging removed for clarity - errors will still be logged

    // Only log essentials
    this.addAnalysisLog(
      `📋 Generated ${allPossibleMoves.length} possible moves`
    );

    // CRITICAL FIX: Track filtered moves through the priority chain
    let currentMoves = allPossibleMoves;
    let totalFiltered = 0;

    // Go through priorities in order
    for (const priority of this.priorities) {
      this.addAnalysisLog(
        `🔍 EXECUTING Priority ${priority.priority}: ${priority.name}`
      );

      // Quick relevance check first
      if (!priority.isRelevant(context)) {
        this.addAnalysisLog(
          `⏭️ Priority ${priority.priority} (${priority.name}) not relevant - skipping`
        );
        continue; // Skip irrelevant priorities silently
      }

      this.addAnalysisLog(
        `✅ Priority ${priority.priority} (${priority.name}) is relevant - analyzing`
      );

      // Update context with current filtered moves
      const updatedContext = {
        ...context,
        allPossibleMoves: currentMoves,
      };

      let result: DecisionResult;
      try {
        result = priority.analyze(updatedContext);
      } catch (error) {
        this.addAnalysisLog(
          `❌ CRITICAL ERROR in Priority ${priority.priority} (${priority.name}): ${error}`
        );
        this.addAnalysisLog(
          `❌ ERROR DETAILS: ${
            error instanceof Error ? error.message : String(error)
          }`
        );

        // CRITICAL: Try to collect any logs that were added before the exception
        if (
          (priority as any).analysisLogs &&
          Array.isArray((priority as any).analysisLogs)
        ) {
          this.addAnalysisLog(
            `🔍 PARTIAL LOGS from ${priority.name} before exception:`
          );
          for (const log of (priority as any).analysisLogs) {
            this.addAnalysisLog(`   ${log}`);
          }
        }

        continue; // Skip this priority due to error
      }

      // Always collect priority analysis logs (whether it takes action or not)
      if (result.diagnostics && result.diagnostics.logs) {
        for (const log of result.diagnostics.logs) {
          this.addAnalysisLog(log);
        }
      }

      // Debug: Track when priorities claim to exit but don't actually exit
      if (result.shouldEarlyExit && !result.selectedMove) {
        this.addAnalysisLog(
          `⚠️ BUG: ${priority.name} claims shouldEarlyExit=true but no selectedMove provided!`
        );
      }

      // CRITICAL FIX: Handle early exits vs filtering differently
      if (result.shouldEarlyExit) {
        // Early exit - stop the chain immediately
        this.addAnalysisLog(
          `🎯 PRIORITY ${priority.priority} (${priority.name}) EARLY EXIT: ${result.reasoning}`
        );

        // Return the result with complete analysis logs
        return {
          ...result,
          diagnostics: {
            ...result.diagnostics,
            logs: this.currentAnalysisLogs,
          },
        };
      }

      if (result.filteredMoves) {
        // Filtering - continue the chain with filtered moves
        const movesFiltered = currentMoves.length - result.filteredMoves.length;
        totalFiltered += movesFiltered;

        this.addAnalysisLog(
          `🎯 PRIORITY ${priority.priority} (${priority.name}) FILTERING: ${result.reasoning} (${movesFiltered} moves filtered)`
        );

        // Update current moves for next priority
        currentMoves = result.filteredMoves;

        // If all moves filtered, no choice but to return
        if (currentMoves.length === 0) {
          this.addAnalysisLog(
            `💀 CRITICAL: All moves filtered by ${priority.name}! No moves remaining.`
          );
          return {
            ...result,
            diagnostics: {
              ...result.diagnostics,
              logs: this.currentAnalysisLogs,
            },
          };
        }

        // Continue to next priority with filtered moves
        continue;
      }

      // If no action, continue to next priority
      this.addAnalysisLog(
        `➡️ Priority ${priority.priority} (${priority.name}) took no action - continuing`
      );
    }

    // If no priorities took action, return current moves (may be filtered) with analysis logs
    if (totalFiltered > 0) {
      this.addAnalysisLog(
        `🤷 No priority made a decision - falling back to Focused MCTS with ${currentMoves.length} moves (${totalFiltered} total filtered)`
      );
    } else {
      this.addAnalysisLog(
        `🤷 No priority made a decision - falling back to Focused MCTS with ${currentMoves.length} moves`
      );
    }

    return {
      filteredMoves: currentMoves,
      selectedMove: undefined,
      shouldEarlyExit: false,
      reasoning:
        totalFiltered > 0
          ? `No priorities made decisions - using ${currentMoves.length} moves after ${totalFiltered} filtered`
          : "No priorities triggered - using all moves for MCTS",
      priority: 999,
      diagnostics: {
        logs: this.currentAnalysisLogs,
      },
    };
  }

  /**
   * Log messages for debugging
   */
  private addAnalysisLog(message: string): void {
    this.currentAnalysisLogs.push(message);
  }

  private log(message: string, context: DecisionContext): void {
    if (context.debugMode) {
    }
  }

  /**
   * Get information about all priorities
   */
  public getPrioritiesInfo(): Array<{
    name: string;
    priority: number;
    description: string;
  }> {
    return this.priorities.map((p) => ({
      name: p.name,
      priority: p.priority,
      description: p.description,
    }));
  }

  /**
   * Toggle debug mode
   */
  public setDebugMode(enabled: boolean): void {
    this.debugMode = enabled;
  }
}
